import React, { useState, ReactElement, useCallback, useMemo } from 'react';
import { Pie } from '@nivo/pie';
import { PieChartData } from '@Types/nivo';
import './ByAttributeTile.scss';

interface ByAttributeTileProps {
    title: string;
    numTasks?: number;
    data: PieChartData[];
}

const CHART_SIZE = 134;

const ByAttributeTile = (props: ByAttributeTileProps): ReactElement => {
    const [isGrown, setIsGrown] = useState<boolean>(false);
    const [rotation, setRotation] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [dragStart, setDragStart] = useState<{ x: number } | null>(null);

    const toggleGrowing = useCallback((): void => {
        setIsGrown((prev) => !prev);
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
        setIsDragging(true);
        setDragStart({ x: e.clientX });
    }, []);

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
        if (!isDragging || !dragStart) return;
        const deltaX = e.clientX - dragStart.x;
        setRotation((prev) => prev + deltaX * 0.5);
    }, [isDragging, dragStart]);

    const handleMouseUp = useCallback((): void => {
        setIsDragging(false);
        setDragStart(null);
    }, []);

    const innerRadius = useMemo(() => (isGrown ? 0.4 : 0.7), [isGrown]);
    const tileClassName = useMemo(
        () => `by-attribute-tile ${isGrown ? 'grow' : 'shrink'}`,
        [isGrown]
    );

    return (
        <div className={tileClassName}>
            <div className='by-attribute-tile-header'>
                <div className='by-attribute-tile-header-title'>{props.title}</div>
                <div className='by-attribute-tile-header-actions'>
                    <i className='fa-regular fa-ellipsis'></i>
                    <i 
                        className={`fa-regular ${isGrown ? 'fa-arrow-left-from-line' : 'fa-arrow-right-from-line'}`}
                        onClick={toggleGrowing}
                    ></i>
                </div>
            </div>
            <div 
                className='by-attribute-tile-body'
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
                <Pie
                    height={CHART_SIZE}
                    width={CHART_SIZE}
                    data={props.data}
                    innerRadius={innerRadius}
                    colors={{ scheme: 'tableau10' }}
                    enableArcLabels={false}
                    enableArcLinkLabels={isGrown}
                    startAngle={rotation}
                    endAngle={rotation + 360}
                    animate={true}
                    isInteractive={true}
                />
            </div>
            <div className='by-attribute-tile-footer'>
                {props.numTasks !== undefined && (
                    <>
                        <div className='by-attribute-tile-footer-icon'>
                            <i className='fa-regular fa-bolt'></i>
                        </div>
                        <div className='by-attribute-tile-footer-text'>{props.numTasks} Tasks</div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ByAttributeTile;
